import { api } from './api.ts';
import { setToolStatus } from './chatTurns.ts';
import { notifyActivityChanged } from './useDeckActivity.ts';
import { setFlipShowing, clearFlipShowing, showingFromWire } from './reviewFlip.ts';
import type { ActivityRow, PreviewStatusPayload } from '../../shared/api.ts';

/** `force` overrides the disk-hash check (something OUTSIDE the AI history
 *  moved the slide); `reapply` puts a reverted change back instead. */
export type RevertOptions = { force?: boolean; reapply?: boolean };

/** A row that targets a slide. The slide-scoped verbs below take this rather
 *  than a bare ActivityRow: `row.slideId` is nullable (a reorder or a
 *  deck-scope run error has none), and building `/_slide/null/preview` would
 *  404 rather than fail anywhere useful. `canPreviewRow` is the check that
 *  proves it. */
export type SlideRow = ActivityRow & { slideId: string };

/**
 * Shared verbs for reviewing an AI proposal (an ActivityRow from
 * /api/chat/:slug/history) — used by the Activity tab rows AND the on-slide
 * proposal banner, so both surfaces behave identically: same endpoints as the
 * chat ToolCard, same status bookkeeping, same refresh signal.
 */

const slideBase = (slug: string, id: string) => `/api/chat/${slug}/_slide/${encodeURIComponent(id)}`;

export function rowPaths(slug: string, row: ActivityRow): { apply: string; reject: string } {
  const deck = row.threadKey === '_deck';
  return {
    apply: deck ? `/api/chat/${slug}/_deck/apply` : `${slideBase(slug, row.threadKey)}/apply`,
    reject: deck ? `/api/chat/${slug}/_deck/reject` : `${slideBase(slug, row.threadKey)}/reject`,
  };
}

function resolve(slug: string, row: ActivityRow, status: string | null) {
  // Optimistic: settles the matching chat card instantly so the two surfaces
  // don't visibly disagree for a beat. It is NOT the correctness mechanism —
  // it no-ops when the row has no toolId (a leftover), when that scope isn't
  // loaded, or when the key doesn't match. Correctness comes from the thread
  // reload notifyActivityChanged triggers (Canvas.handleActivityChanged).
  //
  // Key on the BASE slide id: scope keys built from a focused slide carry the
  // unsplit id (`slides/x.jsx#2`), while a thread key is always the base, so a
  // multi-instance slide missed every patch.
  if (status && row.toolId) {
    const base = String(row.threadKey || '').split('#')[0];
    setToolStatus(`${slug}:${base}`, row.toolId, status);
  }
  notifyActivityChanged();
}

/** Write the proposal to the slide file (live preview). */
export async function previewRow(slug: string, row: SlideRow): Promise<void> {
  await api.post(`${slideBase(slug, row.slideId)}/preview`, {
    tool: row.tool,
    input: row.input,
    tool_id: row.toolId,
    // The card's own thread — a deck-scope card previews on a slide but lives
    // in the deck thread, so the slide id would be the wrong place to mark it.
    owner_thread: row.threadKey,
  });
  // A fresh preview always lands showing the candidate.
  setFlipShowing(slug, row.slideId, 'candidate');
  resolve(slug, row, null);
}

/** Flip the on-disk file between proposed and original. Returns 'proposed'|'original'. */
export async function toggleRow(slug: string, row: SlideRow): Promise<'proposed' | 'original'> {
  const res = await api.post<Partial<PreviewStatusPayload>>(`${slideBase(slug, row.slideId)}/toggle-preview`, {});
  // Flipping the on-disk side changes what every surface should be showing —
  // this was the one verb that told nobody. Now it writes the shared store, so
  // the banner, the Activity row and the chat card move together.
  const wire = res?.showing === 'original' ? 'original' : 'proposed';
  setFlipShowing(slug, row.slideId, showingFromWire(wire));
  resolve(slug, row, null);
  return wire;
}

/** Which side is currently on disk (server truth). */
export async function rowPreviewStatus(slug: string, slideId: string): Promise<PreviewStatusPayload> {
  return api.get<PreviewStatusPayload>(`${slideBase(slug, slideId)}/preview-status`);
}

export async function applyRow(slug: string, row: ActivityRow): Promise<void> {
  await api.post(rowPaths(slug, row).apply, { tool: row.tool, input: row.input, tool_id: row.toolId });
  clearFlipShowing(slug, row.slideId);
  resolve(slug, row, 'applied');
}

export async function rejectRow(slug: string, row: ActivityRow): Promise<void> {
  // Always attempt the preview discard — `row.previewing` can be stale, and
  // skipping it would leave the rejected proposal live on the slide. No-op
  // when nothing previews.
  if (row.slideId) {
    await api.post(`${slideBase(slug, row.slideId)}/cancel-preview`, {}).catch(() => {});
  }
  await api.post(rowPaths(slug, row).reject, { tool_id: row.toolId });
  clearFlipShowing(slug, row.slideId);
  resolve(slug, row, 'rejected');
}

/** Revert an applied change, or (reapply=true) put a reverted one back. */
export async function revertRow(
  slug: string,
  row: ActivityRow,
  { force = false, reapply = false }: RevertOptions = {},
): Promise<void> {
  await api.post(`/api/chat/${slug}/revert`, {
    entryId: row.historyId,
    ...(force ? { force: true } : {}),
    ...(reapply ? { reapply: true } : {}),
  });
  resolve(slug, row, reapply ? 'applied' : 'reverted');
}

/** Show what a revert (side 'before') or re-apply (side 'after') would leave
 *  on the slide, as a live preview — review it before committing. */
export async function revertPreviewRow(slug: string, row: ActivityRow, side: 'before' | 'after'): Promise<void> {
  await api.post(`/api/chat/${slug}/revert-preview`, { entryId: row.historyId, side });
  // The side asked for IS the candidate under review, whichever it was.
  setFlipShowing(slug, row.slideId, 'candidate');
  resolve(slug, row, null);
}

/** Commit the reviewed revert/re-apply: restore the current state to disk
 *  (so the hash check sees what it expects), then perform the revert. */
export async function confirmRevertRow(
  slug: string,
  row: ActivityRow,
  { force = false, reapply = false }: RevertOptions = {},
): Promise<void> {
  if (row.slideId) {
    await api.post(`${slideBase(slug, row.slideId)}/cancel-preview`, {}).catch(() => {});
  }
  await revertRow(slug, row, { force, reapply });
}

/** Abandon a revert/re-apply review — put the current state back. */
export async function cancelRowPreview(slug: string, row: ActivityRow): Promise<void> {
  if (row.slideId) {
    await api.post(`${slideBase(slug, row.slideId)}/cancel-preview`, {}).catch(() => {});
  }
  clearFlipShowing(slug, row.slideId);
  resolve(slug, row, null);
}

/** Put a proposed NEW slide into the deck for review — it lands in the
 *  filmstrip tagged New and renders on the canvas, so the review walk can step
 *  through it like any other proposal. Keeping it is bookkeeping; discarding it
 *  (rejectRow) takes it back out. Returns the id the slide actually got. */
export async function previewCreateRow(slug: string, row: ActivityRow): Promise<string | null> {
  const res = await api.post<{ slideId?: string }>(`/api/chat/${slug}/_deck/preview-create`, {
    input: row.input,
    tool_id: row.toolId,
  });
  resolve(slug, row, null);
  return res?.slideId || null;
}

// edit-type tools carry full slide JSX, so they can live-preview by overwriting
// the slide file; a create has no file to overwrite, so it previews by being
// inserted (previewCreateRow). Structural cards have nothing to show.
export const PREVIEWABLE_TOOLS = new Set(['edit_slide', 'replace_slide_content', 'swap_primitive']);
export const canPreviewRow = (row: ActivityRow): boolean =>
  row.status === 'ready' && !!row.input && !!row.slideId && PREVIEWABLE_TOOLS.has(row.tool);
/** A create proposal not yet inserted for review. */
export const canPreviewCreateRow = (row: ActivityRow): boolean =>
  row.status === 'ready' && !!row.input && row.tool === 'create_slide' && !row.pending;
